import { useRef, useState } from 'react'
import type * as monaco from 'monaco-editor'
import { Check, Download, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { TabBar } from '@/components/TabBar'
import { YamlMonacoEditor } from '@/components/projects/workflows'
import { AgentConfigForm, AgentMemoryScopeForm } from '@/components/projects'
import { AGENT_DESCRIPTION_MAX, AGENT_NAME_MAX, AGENT_YAML_MAX, serializeAgentYaml } from '@/lib/agentYaml'
import { cn } from '@/lib/utils'
import { useAgentDetailContext } from '@/hooks'
import { PENDING_MEMORY_CONFLICT_MESSAGE } from '@/hooks/useAgentDetailContext'

type ConfigView = 'yaml' | 'form' | 'memory-scope'

const configViewTabs: { key: ConfigView; label: string }[] = [
  { key: 'yaml', label: 'YAML' },
  { key: 'form', label: 'Form' },
  { key: 'memory-scope', label: 'Memory Scope' },
]

const labelClass = 'text-xs font-bold leading-[160%] tracking-[0.015em] text-foreground'
const actionButtonClass = 'h-10 rounded-lg px-4'

export function AgentConfigPage() {
  const {
    projectId,
    agent,
    canEdit,
    canReadMemory,
    memoryAgentId,
    onQueueMemory,
    pendingMemoryConflict,
    editorYaml,
    onYamlChange,
    parsedConfig,
    unknownYamlKeys,
    displayName,
    onDisplayNameChange,
    internalName,
    onInternalNameChange,
    internalNameError,
    showValidation,
    description,
    onDescriptionChange,
    missingRequiredFields,
    isDirty,
    isSaving,
    saveBlockedReason,
    onSave,
    onCancel,
    onExport,
  } = useAgentDetailContext()

  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null)
  const [configView, setConfigView] = useState<ConfigView>('yaml')

  const isNew = agent === null
  const fieldsLocked = !canEdit || (isNew && isSaving)
  const displayNameMissing = showValidation && canEdit && !displayName.trim()
  const yamlTooLong = editorYaml.length > AGENT_YAML_MAX
  const showMissingRequiredFields =
    showValidation && configView !== 'form' && parsedConfig !== null && missingRequiredFields.length > 0

  return (
    <div className="flex flex-col gap-6 rounded-2xl border border-border bg-card p-6" data-testid="agent-config-section">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button variant="ghost" onClick={onExport} className={actionButtonClass}>
          <Download className="h-4 w-4" />
          Export YAML
        </Button>
        {canEdit && (
          <>
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={isSaving || (!isNew && !isDirty)}
              className={cn(actionButtonClass, 'border-primary')}
              data-testid="cancel-agent-button"
            >
              <X className="h-4 w-4" />
              Cancel
            </Button>
            <Button
              onClick={onSave}
              disabled={saveBlockedReason !== null}
              title={saveBlockedReason ?? undefined}
              className={actionButtonClass}
              data-testid="save-agent-button"
            >
              <Check className="h-4 w-4" />
              {isSaving ? 'Saving...' : 'Save Agent'}
            </Button>
          </>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="agent-display-name" className={labelClass}>
            Display Name <span aria-hidden="true">*</span>
          </Label>
          <Input
            id="agent-display-name"
            value={displayName}
            onChange={e => onDisplayNameChange(e.target.value)}
            readOnly={fieldsLocked}
            disabled={!canEdit}
            maxLength={AGENT_NAME_MAX}
            placeholder="e.g. Backend Analyst"
            aria-required={!fieldsLocked}
            aria-invalid={displayNameMissing}
            aria-describedby={displayNameMissing ? 'agent-display-name-message' : undefined}
            className={cn('h-10 rounded-lg border-input', displayNameMissing && 'border-destructive')}
            data-testid="agent-display-name-input"
          />
          {displayNameMissing && (
            <p id="agent-display-name-message" className="text-xs text-destructive" role="alert">Display name is required</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="agent-internal-name" className={labelClass}>
            Internal Name <span aria-hidden="true">*</span>
          </Label>
          <Input
            id="agent-internal-name"
            value={internalName}
            onChange={e => onInternalNameChange(e.target.value)}
            readOnly={!isNew || isSaving}
            disabled={!isNew}
            maxLength={AGENT_NAME_MAX}
            placeholder="agent-internal-name"
            aria-required={isNew}
            aria-invalid={internalNameError !== null}
            aria-describedby={internalNameError ? 'agent-internal-name-error agent-internal-name-message' : 'agent-internal-name-message'}
            className={cn('h-10 rounded-lg border-input', internalNameError && 'border-destructive')}
            data-testid="agent-internal-name-input"
          />
          {internalNameError && (
            <p id="agent-internal-name-error" className="text-xs text-destructive" role="alert">
              {internalNameError}
            </p>
          )}
          <p id="agent-internal-name-message" className={cn('text-xs', isNew ? 'text-foreground' : 'text-muted-foreground')}>
            {isNew
              ? "Generated from the Display Name. It can't be changed after the agent is saved."
              : "Workflows reference the agent by this name, so it can't be changed yet."}
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="agent-version" className={cn(labelClass, 'text-muted-foreground')}>
          Version <span aria-hidden="true">*</span>
        </Label>
        <Input
          id="agent-version"
          value={agent?.latestVersion?.version ?? ''}
          readOnly
          disabled
          placeholder="1.0.0"
          aria-describedby="agent-version-message"
          className="h-10 rounded-lg border-input"
          data-testid="agent-version-input"
        />
        <p id="agent-version-message" className="text-xs text-muted-foreground">Assigned automatically on save.</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="agent-description" className={labelClass}>Short Description</Label>
        <Input
          id="agent-description"
          value={description}
          onChange={e => onDescriptionChange(e.target.value)}
          readOnly={fieldsLocked}
          disabled={!canEdit}
          maxLength={AGENT_DESCRIPTION_MAX}
          placeholder="Add a short description"
          className="h-10 rounded-lg border-input"
          data-testid="agent-description-input"
        />
      </div>

      <div className="space-y-2">
        <TabBar tabs={configViewTabs} activeTab={configView} onTabChange={setConfigView} className="w-fit bg-page p-1" />

        {!parsedConfig && (
          <p className="text-sm text-destructive" role="alert" data-testid="yaml-invalid">
            The YAML is invalid, so it can't be saved. Fix the syntax in the YAML tab.
          </p>
        )}
        {yamlTooLong && (
          <p className="text-sm text-destructive" role="alert">
            Agent YAML must not exceed {AGENT_YAML_MAX} characters (currently {editorYaml.length}).
          </p>
        )}
        {showValidation && pendingMemoryConflict && (
          <p className="text-sm text-destructive" role="alert" data-testid="pending-memory-conflict">
            {PENDING_MEMORY_CONFLICT_MESSAGE}
          </p>
        )}
        {showMissingRequiredFields && (
          <p className="text-sm text-destructive" role="alert" data-testid="missing-required-fields">
            Required before saving: {missingRequiredFields.join(' and ')}.
          </p>
        )}

        {configView === 'yaml' && (
          <div className="space-y-2">
            <div className="h-[560px] overflow-hidden rounded-2xl border border-border">
              <YamlMonacoEditor
                value={editorYaml}
                onChange={onYamlChange}
                editorRef={editorRef}
                readOnly={!canEdit}
              />
            </div>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                Every save creates an immutable version — workflows pin a version, so editing never silently changes a running workflow.
              </span>
              <span className={yamlTooLong ? 'font-bold text-destructive' : ''} data-testid="yaml-char-count">
                {editorYaml.length}/{AGENT_YAML_MAX}
              </span>
            </div>
          </div>
        )}

        {configView === 'form' && (
          parsedConfig ? (
            <>
              {unknownYamlKeys.length > 0 && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200" data-testid="unknown-yaml-keys-warning">
                  Editing here rewrites the YAML from the form fields — comments, formatting and keys the form doesn't know ({unknownYamlKeys.join(', ')}) will be dropped. Use the YAML tab to keep them.
                </div>
              )}
              <AgentConfigForm
                projectId={projectId}
                config={parsedConfig}
                onChange={next => onYamlChange(serializeAgentYaml(next))}
                disabled={fieldsLocked}
                showValidation={showValidation}
              />
            </>
          ) : (
            <div className="rounded-2xl border border-dashed border-border bg-table-row p-8 text-center text-sm text-muted-foreground" data-testid="form-yaml-invalid">
              Fix the YAML syntax to edit these fields.
            </div>
          )
        )}

        {configView === 'memory-scope' && (
          parsedConfig ? (
            <AgentMemoryScopeForm
              projectId={projectId}
              memoryAgentId={memoryAgentId}
              config={parsedConfig}
              onChange={next => onYamlChange(serializeAgentYaml(next))}
              canAddMemory={canEdit && canReadMemory}
              onQueueMemory={onQueueMemory}
              disabled={fieldsLocked}
              addMemoryDisabled={isSaving}
            />
          ) : (
            <div className="rounded-2xl border border-dashed border-border bg-table-row p-8 text-center text-sm text-muted-foreground" data-testid="memory-scope-yaml-invalid">
              Fix the YAML syntax to edit the memory scope.
            </div>
          )
        )}
      </div>
    </div>
  )
}
